import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const PRIVACY_EMAIL = "privacy@homerangehunt.com";

const SECTIONS = [
  {
    n: 1, title: "Information We Collect",
    body: [
      "We may collect information you provide directly to HomeRange, including:",
      { list: ["Name", "Email address", "Phone number", "Username and account information", "Profile information", "General location information", "Hunting interests and preferences", "Content, listings, photos, reviews, comments, or other information you submit", "Communications you send to HomeRange", "Business information provided by brands, outfitters, guides, organizations, or other partners", "Purchase, subscription, or transaction information"] },
      "When payments are processed through a third-party payment processor, HomeRange may receive information about the transaction without receiving or storing your complete payment-card information.",
      "We may also automatically collect certain technical information when you use the Platform, such as your IP address, browser type, device type, operating system, pages visited, referring pages, approximate location derived from your IP address, and interactions with the Platform.",
    ],
  },
  {
    n: 2, title: "How We Use Information",
    body: [
      "HomeRange may use collected information to:",
      { list: ["Create and manage user accounts", "Operate, maintain, and improve the Platform", "Connect hunters with brands, outfitters, hunting opportunities, resources, and other relevant content", "Display user-submitted listings or content", "Process purchases, subscriptions, advertising, or other transactions", "Communicate with users about their accounts or activity", "Provide customer support", "Send marketing or promotional communications where permitted by law", "Personalize content and recommendations", "Analyze Platform performance and usage", "Prevent fraud, abuse, security threats, and unauthorized activity", "Enforce our Terms of Service and other policies", "Comply with legal and regulatory requirements"] },
    ],
  },
  {
    n: 3, title: "Cookies and Similar Technologies",
    body: [
      "HomeRange may use cookies and similar technologies to keep users signed in, remember preferences, understand how visitors use the Platform, improve functionality, measure performance, and support advertising or marketing activities.",
      "Where required by applicable law, users will be provided with appropriate choices regarding non-essential cookies.",
    ],
  },
  {
    n: 4, title: "How We Share Information",
    body: [
      "HomeRange does not sell personal information for money.",
      "We may share information when reasonably necessary with:",
      { list: ["Service providers that help operate the Platform", "Hosting and technology providers", "Payment processors", "Analytics providers", "Email and communication providers", "Advertising or marketing service providers", "Professional advisers such as attorneys or accountants", "Government authorities when required by law"] },
      "We may also share information when necessary to protect HomeRange, our users, or others from fraud, security threats, illegal activity, or harm.",
      "If HomeRange is involved in a merger, acquisition, financing, reorganization, sale of assets, or similar business transaction, information may be transferred as part of that transaction.",
    ],
  },
  {
    n: 5, title: "Public Content",
    body: [
      "Certain portions of HomeRange are designed for community interaction and discovery.",
      "Information you intentionally make public—including profile information, listings, photos, reviews, comments, gear posts, or other user-generated content—may be visible to other users and potentially to the general public.",
      "Users should avoid posting sensitive personal information publicly.",
    ],
  },
  {
    n: 6, title: "Brands, Outfitters, Agencies, and Third Parties",
    body: [
      "HomeRange may provide links, listings, advertisements, or connections to third-party brands, outfitters, guides, retailers, government agencies, organizations, and websites.",
      "When you leave HomeRange or interact directly with a third party, that organization’s own privacy practices may apply. HomeRange is not responsible for the privacy practices or content of independently operated third-party websites or services.",
    ],
  },
  {
    n: 7, title: "Payments",
    body: [
      "Payments offered through HomeRange may be processed by third-party payment providers.",
      "Those providers may collect payment-card details, billing information, and other information necessary to complete a transaction. Their collection and use of that information are governed by their respective privacy policies.",
      "HomeRange does not intend to directly store complete credit or debit card numbers unless specifically disclosed otherwise.",
    ],
  },
  {
    n: 8, title: "Data Security",
    body: [
      "HomeRange uses reasonable administrative, technical, and organizational safeguards designed to protect personal information.",
      "However, no website, internet transmission, or electronic storage system can be guaranteed to be completely secure. Users are responsible for protecting their account credentials and should notify HomeRange if they believe their account has been compromised.",
    ],
  },
  {
    n: 9, title: "Data Retention",
    body: [
      "We may retain personal information for as long as reasonably necessary to provide our services, maintain legitimate business records, resolve disputes, enforce agreements, prevent fraud, and comply with applicable legal obligations.",
      "Retention periods may vary depending on the type of information and the reason it was collected.",
    ],
  },
  {
    n: 10, title: "Your Privacy Choices",
    body: [
      "Depending on where you live, you may have the right to request that HomeRange:",
      { list: ["Provide access to certain personal information we maintain about you", "Correct inaccurate personal information", "Delete certain personal information", "Provide a copy of certain information", "Limit or object to certain uses of your information", "Allow you to opt out of certain targeted advertising or data-sharing practices"] },
      "These rights vary by jurisdiction and may be subject to legal exceptions.",
      "HomeRange may need to verify your identity before completing certain privacy requests.",
    ],
  },
  {
    n: 11, title: "California Privacy Rights",
    body: [
      "California residents may have additional rights under applicable California privacy laws.",
      "Depending on HomeRange’s activities and whether applicable statutory thresholds are met, these rights may include requests to know, correct, or delete personal information and rights relating to certain forms of sharing or targeted advertising.",
      "HomeRange will honor privacy rights required by applicable law.",
    ],
  },
  {
    n: 12, title: "Children’s Privacy",
    body: [
      "HomeRange is not intended for children under 13 years of age, and we do not knowingly collect personal information from children under 13.",
      "If we learn that personal information from a child under 13 has been collected without appropriate authorization, we will take reasonable steps to delete it.",
      "Certain hunting activities, purchases, licenses, contests, or other features may have additional age restrictions.",
    ],
  },
  {
    n: 13, title: "Email and Marketing Communications",
    body: [
      "Users who receive promotional emails from HomeRange may unsubscribe using the unsubscribe option included in those communications.",
      "We may continue sending non-promotional communications when necessary, including account, security, transaction, or service-related messages.",
    ],
  },
  {
    n: 14, title: "Changes to This Privacy Policy",
    body: [
      "HomeRange may update this Privacy Policy as the Platform develops or as legal requirements change.",
      "When material changes are made, we may update the “Last Updated” date and provide additional notice when required by law.",
      "Continued use of HomeRange after an updated policy becomes effective constitutes acknowledgment of the updated policy to the extent permitted by law.",
    ],
  },
];

function Block({ item }) {
  if (typeof item === "string") return <p className="text-charcoal/70 leading-relaxed mt-3">{item}</p>;
  if (item.list) return (
    <ul className="mt-3 space-y-1.5">
      {item.list.map((li, i) => (
        <li key={i} className="text-charcoal/70 leading-relaxed pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:bg-blaze">{li}</li>
      ))}
    </ul>
  );
  return null;
}

export default function Privacy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-paper" data-testid="privacy-page">
      <SiteHeader />

      <section className="bg-forest text-bone grain relative overflow-hidden px-5 sm:px-10 py-16 sm:py-20">
        <div className="relative z-10 max-w-[820px] mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-bone/60 hover:text-bone mb-6" data-testid="privacy-back"><ArrowLeft size={15} /> Back to HomeRange</Link>
          <div className="flex items-center gap-2 text-blaze"><ShieldCheck size={18} /><span className="hr-overline">Legal</span></div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 tracking-tight">Privacy Policy</h1>
          <p className="text-bone/60 mt-4 text-sm">Effective Date: August 15, 2026 · Last Updated: August 15, 2026</p>
        </div>
      </section>

      <section className="px-5 sm:px-10 py-14 sm:py-20">
        <div className="max-w-[820px] mx-auto">
          <p className="text-charcoal/70 leading-relaxed">
            HomeRange (“HomeRange,” “we,” “our,” or “us”) respects your privacy and is committed to protecting the personal information of hunters, outdoor enthusiasts, brands, outfitters, agencies, and other users of the HomeRange platform.
          </p>
          <p className="text-charcoal/70 leading-relaxed mt-3">
            This Privacy Policy explains what information we collect, how we use it, when we may share it, and the choices you have regarding your information when you use the HomeRange website, services, and related features (collectively, the “Platform”).
          </p>

          {SECTIONS.map((s) => (
            <div key={s.n} className="mt-10" data-testid={`privacy-section-${s.n}`}>
              <h2 className="font-serif text-2xl text-forest">{s.n}. {s.title}</h2>
              {s.body.map((b, i) => <Block key={i} item={b} />)}
            </div>
          ))}

          {/* Contact */}
          <div className="mt-10" data-testid="privacy-section-15">
            <h2 className="font-serif text-2xl text-forest">15. Contact HomeRange</h2>
            <p className="text-charcoal/70 leading-relaxed mt-3">Questions, concerns, or privacy requests regarding this Privacy Policy may be submitted to:</p>
            <div className="mt-4 border border-saddle/20 bg-white p-5">
              <p className="font-serif text-lg text-forest">HomeRange</p>
              <p className="text-charcoal/70 mt-1 text-sm">Website: <a href="https://homerangehunt.com" className="text-blaze hover:underline">homerangehunt.com</a></p>
              <p className="text-charcoal/70 text-sm">Email: <a href={`mailto:${PRIVACY_EMAIL}`} className="text-blaze hover:underline" data-testid="privacy-email">{PRIVACY_EMAIL}</a></p>
            </div>
          </div>

          <p className="text-charcoal/45 text-xs leading-relaxed mt-10 border-t border-saddle/20 pt-6">
            This Privacy Policy describes HomeRange’s current data practices and should be reviewed and customized as the Platform’s final payment processor, analytics tools, advertising system, and data practices are established. It is provided for general informational purposes and is not legal advice or a substitute for review by a qualified attorney.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
